import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/KeycloakContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

const VALID_ORG_ROLES = [
  'org_owner',
  'org_admin',
  'org_sales_manager',
  'org_sales_member',
  'org_researcher',
  'org_viewer',
  'org_member',
];

const WorkspaceProtectedRoute = ({
  children,
  requireOrganization = false,
  fallbackPath = '/access-denied',
}) => {
  const { keycloak } = useAuth();
  const {
    loading,
    workspaceId,
    isOrganizationWorkspace,
    workspaceRole,
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    isSalesMember,
    isResearcher,
    isViewer,
    isOrganizationUser,
    isSuperadmin,
    isPlatformAdmin,
  } = useWorkspace();
  const location = useLocation();

  if (!keycloak?.authenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Prevent race condition: wait for workspace context to load from backend
  if (loading) {
    return (
      <div
        className="workspace-loading-spinner"
        style={{ padding: '60px 20px', textAlign: 'center' }}
      >
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '28px', color: '#6366f1' }} />
        <p style={{ marginTop: '16px', color: '#64748b', fontSize: '14px' }}>
          Loading workspace context...
        </p>
      </div>
    );
  }

  if (!workspaceId) {
    return <Navigate to="/access-denied" replace state={{ from: location.pathname }} />;
  }

  if (requireOrganization && !isOrganizationWorkspace) {
    return <Navigate to={fallbackPath} replace state={{ from: location.pathname }} />;
  }

  const allowedForWorkspace =
    !isOrganizationWorkspace ||
    isSuperadmin ||
    isPlatformAdmin ||
    isOwner ||
    isOrganizationAdmin ||
    isSalesManager ||
    isSalesMember ||
    isResearcher ||
    isViewer ||
    isOrganizationUser ||
    VALID_ORG_ROLES.includes(workspaceRole);

  if (!allowedForWorkspace) {
    return <Navigate to={fallbackPath} replace state={{ from: location.pathname }} />;
  }

  return children;
};

export default WorkspaceProtectedRoute;
